import { CVEditingSection, FullCV } from '@cibli/shared';
import '@micdrop/web/silero';
import { Micdrop, MicdropConversationMessage, MicdropState } from '@micdrop/web';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useCVTemplateContext } from '@/features/common/contexts/cv-template.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import { resolveWsUrl } from '@/utils/resolveWsUrl';

interface CVEditContextType {
  startVocalEditing: (sectionType: CVEditingSection, sectionContent?: Partial<FullCV>) => void;
  stopVocalEditing: () => void;
  isStartingVocalEditing: boolean;
  lastMessage?: string;
  onVocalUpdate?: (updatedCv: Partial<FullCV>) => void;
  setOnVocalUpdate: (callback: ((updatedCv: Partial<FullCV>) => void) | undefined) => void;
  currentEditingSection: CVEditingSection | null;
  setCurrentEditingSection: (section: CVEditingSection | null) => void;
  currentSectionContent: Partial<FullCV> | null;
  setCurrentSectionContent: (content: Partial<FullCV> | null) => void;
}

const VocalCvEditContext = createContext<CVEditContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useVocalCVEditContext = () => {
  const context = useContext(VocalCvEditContext);
  if (!context) {
    throw new Error('useVocalCVEditContext must be used within CVEditProvider');
  }
  return context;
};

interface Props {
  children: ReactNode;
}

export const VocalCVEditProvider = ({ children }: Props) => {
  const { user } = useUserContext();
  // Langue du CV (choisie par le candidat) : la session vocale doit parler,
  // écouter et extraire dans cette langue, même si la locale du compte
  // reste la locale de base de la cabine.
  const { currentLocaleCv } = useCVTemplateContext();
  const [isStartingVocalEditing, setStartingVocalEditing] = useState(false);
  const [lastMessage, setLastMessage] = useState<string | undefined>();
  const [onVocalUpdate, setOnVocalUpdate] = useState<
    ((updatedCv: Partial<FullCV>) => void) | undefined
  >();
  const [currentEditingSection, setCurrentEditingSection] = useState<CVEditingSection | null>(null);
  const [currentSectionContent, setCurrentSectionContent] = useState<Partial<FullCV> | null>(null);
  // Track the last processed edit_complete message to avoid reprocessing it
  const lastProcessedEditCompleteRef = useRef<MicdropConversationMessage | null>(null);

  const startVocalEditing = useCallback(
    (sectionType: CVEditingSection, sectionContent?: Partial<FullCV>) => {
      if (!user?.id) {
        console.error('User not found');
        return;
      }

      if (isStartingVocalEditing) {
        return;
      }

      if (Micdrop.isStarted) {
        // Reset the last processed edit_complete message when restarting
        lastProcessedEditCompleteRef.current = null;
        Micdrop.stop();

        setTimeout(() => {
          setStartingVocalEditing(true);
          startConnection();
        }, 200);
        return;
      }

      // Reset the last processed edit_complete message when starting
      lastProcessedEditCompleteRef.current = null;
      setStartingVocalEditing(true);
      startConnection();

      function startConnection() {
        if (!user?.id) {
          console.error('User not found');
          return;
        }

        const url = resolveWsUrl();
        url.pathname = '/cv-edit';

        console.warn(
          `[Micdrop][Connecting][CV-Edit] Connexion vers ${url.toString()} (section: ${sectionType})`,
        );
        console.warn(
          '[Micdrop][Models][CV-Edit] Modèle VAD: [silero, volume] (Silero VAD v5 embarqué)',
        );

        Micdrop.start({
          url: url.toString(),
          params: {
            userId: user.id,
            sectionType,
            localeCode: currentLocaleCv,
            ...(sectionContent ? { sectionContent } : {}),
          },
          vad: ['silero', 'volume'],
          disableInterruption: true,
          debugLog: true,
        }).catch(err => {
          console.error('[Micdrop][Start Error][CV-Edit] Impossible de démarrer Micdrop:', err);
        });
      }
    },
    [user?.id, isStartingVocalEditing, currentLocaleCv],
  );

  const stopVocalEditing = useCallback(() => {
    setStartingVocalEditing(false);
    // Reset the last processed edit_complete message when stopping
    lastProcessedEditCompleteRef.current = null;
    if (Micdrop.isStarted) {
      Micdrop.stop();
    }
  }, []);

  const handleStateChange = useCallback(
    async (state: MicdropState, prevState?: MicdropState) => {
      // Log connection status transitions
      if (prevState && prevState.isStarted !== state.isStarted) {
        console.warn(
          `[Micdrop][Connection][CV-Edit] isStarted: ${prevState.isStarted} -> ${state.isStarted}`,
        );
        if (state.isStarted) {
          const vadNames = Micdrop.vad?.name ?? 'unknown';
          console.warn(
            `[Micdrop][VAD Model][CV-Edit] VAD actif: ${vadNames} (Silero VAD v5 local / ONNX)`,
          );
        }
      }
      if (state.error && state.error !== prevState?.error) {
        console.error('[Micdrop][State Error][CV-Edit]', state.error);
      }

      // As soon as Micdrop is started, drop "starting" state so UI reflects actual state
      if (Micdrop.isStarted) {
        setStartingVocalEditing(false);
      }

      const message = state.conversation.findLast(({ role }) => role === 'assistant') as
        MicdropConversationMessage | undefined;
      if (!message) return;

      if (message.metadata) {
        const stt = message.metadata.stt || message.metadata.sttProvider;
        const llm = message.metadata.model || message.metadata.llm || message.metadata.provider;
        const tts = message.metadata.tts || message.metadata.ttsProvider;
        if (stt || llm || tts) {
          console.warn(
            `[Micdrop][Models][CV-Edit] STT: ${stt || 'défaut'} | LLM: ${llm || 'défaut'} | TTS: ${tts || 'défaut'}`,
          );
        }
      }

      const short = message.metadata?.extracted?.short as string | undefined;
      if (short) {
        setLastMessage(short);
      }

      if (message.role === 'assistant' && message.metadata?.type === 'edit_complete') {
        // Only process this edit_complete message if we haven't already processed it
        // This prevents reprocessing the same message when handleStateChange is called again
        if (lastProcessedEditCompleteRef.current === message) {
          return;
        }

        lastProcessedEditCompleteRef.current = message;
        const updatedContent = message.metadata?.updatedContent as Partial<FullCV> | undefined;

        if (onVocalUpdate && updatedContent) {
          onVocalUpdate(updatedContent);
        }
      }
    },
    [onVocalUpdate],
  );

  useEffect(() => {
    const handleError = (error: unknown) => {
      console.error('[Micdrop][Error][CV-Edit]', error);
    };

    Micdrop.on('Error', handleError);
    Micdrop.on('StateChange', handleStateChange);

    return () => {
      Micdrop.off('Error', handleError);
      Micdrop.off('StateChange', handleStateChange);
    };
  }, [handleStateChange]); // Empty deps - we use ref to access latest callback

  return (
    <VocalCvEditContext.Provider
      value={{
        startVocalEditing,
        stopVocalEditing,
        isStartingVocalEditing,
        lastMessage,
        onVocalUpdate,
        setOnVocalUpdate,
        currentEditingSection,
        setCurrentEditingSection,
        currentSectionContent,
        setCurrentSectionContent,
      }}
    >
      {children}
    </VocalCvEditContext.Provider>
  );
};
